'use client'

import { createContext, useContext, useMemo } from 'react'
import { DEFAULT_REQUIRED_FIELDS, DEFAULT_SLA_HOURS } from './types'
import { useMachine } from '@xstate/react'
import { ticketCategoryListMachine } from './machines/ticketCategoryListMachine'
import type {
  TicketCategory,
  TicketMainCategory,
  TicketSubCategory,
  SelectedCategory,
} from './types'

export type TicketCategoryListMachineApi = ReturnType<
  typeof useMachine<typeof ticketCategoryListMachine>
>

const TicketCategoryListMachineContext = createContext<TicketCategoryListMachineApi | null>(null)

export function TicketCategoryListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(ticketCategoryListMachine)
  return (
    <TicketCategoryListMachineContext.Provider value={api}>
      {children}
    </TicketCategoryListMachineContext.Provider>
  )
}

export function useTicketCategoryListMachine() {
  const ctx = useContext(TicketCategoryListMachineContext)
  if (!ctx) {
    throw new Error(
      'useTicketCategoryListMachine must be used within TicketCategoryListMachineProvider'
    )
  }
  return ctx
}

export function useTicketCategoryListMachineOptional() {
  return useContext(TicketCategoryListMachineContext)
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useTicketCategoryListStoreCompat() {
  const machineApi = useTicketCategoryListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const categories = state.context.categories
    const selectedCategory = state.context.selectedCategory

    const getCategoryById = (id: string) => categories.find((cat) => cat.id === id)
    const getCategoriesByMain = (mainCategory: TicketMainCategory) =>
      categories.filter((cat) => cat.mainCategory === mainCategory && cat.isActive)
    const getCategory = (
      main: TicketMainCategory,
      sub: TicketSubCategory
    ): TicketCategory | undefined =>
      categories.find((cat) => cat.mainCategory === main && cat.subCategory === sub && cat.isActive)

    return {
      categories,
      selectedCategory,
      setCategories: (c: TicketCategory[]) => send({ type: 'SET_CATEGORIES', categories: c }),
      addCategory: (category: TicketCategory) => send({ type: 'ADD_CATEGORY', category }),
      updateCategory: (id: string, updates: Partial<TicketCategory>) =>
        send({ type: 'UPDATE_CATEGORY', id, updates }),
      deleteCategory: (id: string) => send({ type: 'DELETE_CATEGORY', id }),
      setSelectedCategory: (category: SelectedCategory | null) =>
        send({ type: 'SET_SELECTED_CATEGORY', category }),
      getCategoryById,
      getCategoriesByMain,
      getCategory,
    }
  }, [machineApi])
}

export function useTicketCategoryActions() {
  const machineApi = useTicketCategoryListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setCategories: (categories: TicketCategory[]) =>
      send({ type: 'SET_CATEGORIES', categories }),
    addCategory: (category: TicketCategory) => send({ type: 'ADD_CATEGORY', category }),
    updateCategory: (id: string, updates: Partial<TicketCategory>) =>
      send({ type: 'UPDATE_CATEGORY', id, updates }),
    deleteCategory: (id: string) => send({ type: 'DELETE_CATEGORY', id }),
    setSelectedCategory: (category: SelectedCategory | null) =>
      send({ type: 'SET_SELECTED_CATEGORY', category }),
  }), [send])
}

/** Hook to get required fields for selected category */
export function useRequiredFieldsForCategory(category: SelectedCategory | null) {
  const { getCategory } = useTicketCategoryListStoreCompat()
  if (!category) return []
  const cat = getCategory(category.mainCategory, category.subCategory)
  return cat?.requiredFields || DEFAULT_REQUIRED_FIELDS[category.mainCategory]
}

/** Hook to get SLA hours for category */
export function useSlaHoursForCategory(category: SelectedCategory | null) {
  const { getCategory } = useTicketCategoryListStoreCompat()
  if (!category) return 24
  const cat = getCategory(category.mainCategory, category.subCategory)
  return cat?.slaHours ?? DEFAULT_SLA_HOURS[category.mainCategory]
}
